import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Reveal, MaskLines, SectionMark } from "./ui/Primitives";
import { featuredServices } from "../data/services";

const topServices = featuredServices.map((s) => ({
  title: s.title,
  desc: s.short,
  stack: s.tags.slice(0, 3).join(" · "),
}));

const HomeServicesPreview: React.FC = () => {
  const [active, setActive] = useState<number | null>(null);

  return (
    <section className="relative bg-ink py-24 lg:py-36">
      <div className="shell">
        <Reveal>
          <SectionMark label="מה אנחנו עושים" index="01" />
        </Reveal>

        <MaskLines
          as="h2"
          className="t-h1 mb-14 text-fg"
          lines={["שירותים שמניעים", "צמיחה עסקית"]}
        />

        {/* An index, not a card grid: each service is a row you can scan in one
            pass, and the row itself is the affordance. */}
        <ul className="border-t border-[var(--line)]">
          {topServices.map((service, i) => {
            const isActive = active === i;
            return (
              <Reveal as="li" key={service.title} delay={i * 60}>
                <Link
                  to="/services"
                  onMouseEnter={() => setActive(i)}
                  onMouseLeave={() => setActive(null)}
                  onFocus={() => setActive(i)}
                  onBlur={() => setActive(null)}
                  className="group relative block border-b border-[var(--line)] py-6 sm:py-7"
                >
                  {/* The rule at the top of the row draws in from the reading edge */}
                  <span
                    aria-hidden="true"
                    className={`absolute inset-x-0 top-0 h-px origin-right bg-signal transition-transform duration-500 ease-out-expo ${
                      isActive ? "scale-x-100" : "scale-x-0"
                    }`}
                  />

                  <div
                    className="flex flex-col gap-2 transition-transform duration-500 ease-out-expo sm:grid sm:grid-cols-[3rem_minmax(0,1fr)_minmax(0,1fr)_2rem] sm:items-baseline sm:gap-6"
                    style={{ transform: isActive ? "translateX(-10px)" : "none" }}
                  >
                    <span
                      className={`t-data text-xs transition-colors duration-300 ${
                        isActive ? "text-signal" : "text-fg-3"
                      }`}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>

                    <h3
                      className={`t-h3 transition-colors duration-300 ${
                        isActive ? "text-signal" : "text-fg"
                      }`}
                    >
                      {service.title}
                    </h3>

                    <div className="min-w-0">
                      <p className="text-fg-2">{service.desc}</p>
                      <p className="t-data mt-1.5 text-[0.68rem] tracking-wider text-fg-3">
                        {service.stack}
                      </p>
                    </div>

                    <ArrowLeft
                      aria-hidden="true"
                      className={`hidden h-4 w-4 justify-self-start transition-all duration-500 ease-out-expo sm:block ${
                        isActive
                          ? "-translate-x-1 text-signal opacity-100"
                          : "text-fg-3 opacity-0"
                      }`}
                    />
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </ul>

        <Reveal delay={120}>
          <div className="mt-12">
            <Link to="/services" className="btn btn-ghost">
              <span>כל השירותים שלנו</span>
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default HomeServicesPreview;
